<script setup>
import {
  defineProps,
  ref,
  watch,
  defineEmits,
  computed,
  onMounted,
  reactive,
} from "vue";
import { getFormInputs, getContactEntries } from "@/api.js";
// import HyphenCheckBox from "./HyphenCheckBox.vue";

const props = defineProps({
  id: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    default: "",
  },
  value: {
    default: [],
  },
  width: {
    type: String,
    default: "auto",
  },
  height: {
    type: String,
    default: "auto",
  },
  backgroundColor: {
    type: String,
    default: "transparent",
  },
  description: {
    type: String,
    default: "",
  },
  inputStyle: {
    type: Object,
    default: () => {},
  },
  required: {
    type: Boolean,
    default: false,
  },
  optionLabel: {
    type: String,
    default: "",
  },
  optionValue: {
    type: String,
    default: "",
  },
  action: {
    type: String || null,
    default: null,
  },
  editable: {
    type: Boolean,
    default: true,
  },
  source: {
    type: String,
    default: "",
  },
  data: {
    type: Array,
    default: () => [],
  },
  choice: {
    type: String,
    default: "",
  },
});

//  "source": "form",
//       "choice": "hypn62yfz702613",
//       "data": "email"

const options = ref([]);

const isLoading = ref(false);

const inputValue = ref([]);

const showError = ref(false);

const canShowError = computed(() => {
  return showError.value && collectFeedback.value.error.hasError;
});

const collectFeedback = computed(() => {
  let error = {
    hasError: false,
    message: "",
  };

  if (inputValue.value.length === 0 && props.required) {
    error.hasError = true;
    error.message = `Select atleast one ${props.label} option in the table`;
  }

  return {
    error,
  };
});

const emit = defineEmits({
  input(val) {
    return val;
  },
  feedback(val) {
    return val;
  },
  changed(val) {
    return val;
  },
});

const paginationInfo = reactive({
  page: 0,
  totalPages: 0,
});

watch(
  inputValue,
  (val) => {
    emit("input", val);
    emit("feedback", collectFeedback.value);
  },
  { immediate: true }
);

watch(
  () => props.action,
  (val) => {
    switch (val) {
      case "showError":
        showError.value = true;
        break;
      case "hideError":
        showError.value = false;
        break;
      default:
        showError.value = false;
        break;
    }
  },
  { immediate: true }
);

const fetchFormsEntries = async () => {
  try {
    isLoading.value = true;
    if (props.source === "form") {
      const response = await getFormInputs({
        label: props.data.map((data) => data.value).join(","),
        category: props.choice,
        page: paginationInfo.page,
      });

      options.value = response.data.data;
      paginationInfo.page = response.data.page;
      paginationInfo.totalPages = response.data.totalPages;
    } else if (props.source === "contacts") {
      const response = await getContactEntries({
        label: props.data,
        category: props.choice,
      });

      options.value = response.data.data.map((data) => {
        return {
          label: data.label || "Unkwown",
          value: data.id,
        };
      });
    }
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.data,
  (val) => {
    if (val) {
      fetchFormsEntries();
    }
  },
  { immediate: true }
);

const table = ref(null);
const container = ref(null);

const fetchNew = (page) => {
  paginationInfo.page = page;
  fetchFormsEntries();
};

const pageRange = computed(() => {
  const range = 2; // Adjust this value to limit the range
  let start = Math.max(1, paginationInfo.page - range);
  let end = Math.min(paginationInfo.totalPages, paginationInfo.page + range);

  if (start === 1) {
    end = Math.min(
      end + (range - paginationInfo.page + 1),
      paginationInfo.totalPages
    );
  } else if (end === paginationInfo.totalPages) {
    start = Math.max(
      start - (range - (paginationInfo.totalPages - paginationInfo.page)),
      1
    );
  }

  const pageRange = Array.from(
    { length: end - start + 1 },
    (_, i) => start + i
  );

  return pageRange.length > 0 ? pageRange : [];
});

const activeRow = ref(-1);

const rowClick = (id) => {
  const index = inputValue.value.indexOf(id);
  if (index > -1) {
    inputValue.value.splice(index, 1);
  } else {
    inputValue.value.push(id);
  }
};

onMounted(() => {
  container.value.style.width = table.value.offsetWidth + "px";
  container.value.style.display = "block";
});

watch(
  () => props.value,
  (val) => {
    if (
      Array.isArray(val) &&
      JSON.stringify(val) !== JSON.stringify(inputValue.value)
    ) {
      inputValue.value = val;
    }
  },
  { immediate: true },
  { deep: true }
);

const innerKeys = (obj) => {
  return Object.keys(obj)
    .join(" ")
    .replace("id", "")
    .split(" ")
    .filter((item) => item !== "")
    .join(" ")
    .split(" ");
};

const toggleMore = (e, i) => {
  e.stopPropagation();
  activeRow.value = activeRow.value === i ? -1 : i;
};

const datax = (val) => {
  const res = options.value.map((option) => {
    return {
      ...option.labelData,
    };
  })[0][val];

  return Array.isArray(res);
};
</script>

<template>
  <div class="hyphen-table" ref="table" :style="{ width: width }">
    <label class="hyphen-select__label" :for="props.id">{{
      props.label
    }}</label>
    <div class="hyphen-table__body">
      <div v-if="props.description" class="hyphen-table-description">
        {{ props.description }}
      </div>
      <div class="table-wrapper" ref="container">
        <div v-if="!isLoading" class="table-wrapper__content">
          <div class="table-header">
            <span class="action-block"></span>
            <span
              class="cell"
              v-for="(header, x) in props.data"
              :key="x"
              :class="{
                list: datax(header.value),
              }"
            >
              {{ header.label }}
            </span>
          </div>
          <div
            v-for="(option, i) in options"
            :key="i"
            class="table-row"
            @click="rowClick(option.id)"
          >
            <span class="action-block">
              <label :id="option.id" class="hyphen-checkbox">
                <input
                  type="checkbox"
                  v-model="inputValue"
                  :id="option.id"
                  :value="option.id"
                />
                <span class="checkmark"></span>
              </label>
            </span>

            <span
              class="cell"
              v-for="x in Object.keys(option.labelData)"
              :key="x"
            >
              <div class="list-block" v-if="Array.isArray(option.labelData[x])">
                <div
                  v-for="item in option.labelData[x].slice(
                    0,
                    activeRow === i ? option.labelData[x].length : 1
                  )"
                  :key="item"
                >
                  <span v-for="key in innerKeys(item)" :key="key" :title="key">
                    {{ item[key] }}
                  </span>
                </div>

                <button @click="toggleMore($event, i)" type="button">
                  {{ activeRow === i ? "Show Less" : "Show More" }}
                </button>
              </div>
              <template v-else>
                {{ option.labelData[x] || "N/A" }}
              </template>
            </span>
          </div>
        </div>
      </div>
      <div v-if="isLoading" class="loading-data">loading data...</div>
      <div v-if="pageRange.length > 1" class="hyphen-table-footer">
        <div class="pagination">
          <button @click="fetchNew(1)" v-if="paginationInfo.page > 1">
            first
          </button>
          <button
            v-if="paginationInfo.page > 1"
            @click="fetchNew(paginationInfo.page - 1)"
          >
            prev
          </button>
          <button
            v-for="page in pageRange"
            @click="fetchNew(page)"
            :key="page"
            :class="{ active: page === paginationInfo.page }"
          >
            {{ page }}
          </button>
          <button
            @click="fetchNew(paginationInfo.page + 1)"
            v-if="paginationInfo.page < paginationInfo.totalPages"
          >
            next
          </button>
          <button
            @click="fetchNew(paginationInfo.totalPages)"
            v-if="paginationInfo.page < paginationInfo.totalPages"
          >
            last
          </button>
        </div>
      </div>
    </div>

    <div v-if="canShowError" class="hyphen-table__error-text">
      {{ collectFeedback.error.message }}
    </div>
  </div>
</template>
<style lang="scss" scoped>
.hyphen-table {
  display: flex;
  flex-direction: column;
  font-family: "Inter";
  gap: 6px;
  width: 100%;
  box-sizing: border-box;

  &__label {
    color: rgba(25, 40, 61, 0.8);
    font-size: 14px;
    font-style: normal;
    line-height: normal;
    font-weight: 500;
  }

  &__body {
    border: 1.8px solid #d9dee1;
    border-radius: 5px;
    width: 100%;
    padding: 0;
    box-sizing: border-box;
    overflow: hidden;
    .hyphen-table-description {
      padding: 26px;
      font-size: 12px;
      color: #797e86;
      color: #19283d;
      font-family: "Inter";
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 22px; /* 183.333% */
    }

    .table-wrapper {
      overflow: auto;
      display: none;

      &__content {
        display: flex;
        flex-direction: column;
        width: auto;

        .table-header {
          display: flex;
          align-items: center;
          height: 42px;
          width: fit-content;
          justify-content: space-around;
          min-width: 100%;
          background: #d9dee1;
          gap: 5px;
          position: relative;

          .action-block {
            background: #d9dee1;
            position: sticky;
            left: -1px;
            top: 0;
            height: 42px;
            width: 42px !important;
          }

          .cell {
            flex: 1;
            padding: 5px 0;
            min-width: 220px;
            max-width: 400px;
            box-sizing: border-box;
            white-space: nowrap;
            color: rgba(25, 40, 61, 0.8);
            font-family: "Inter";
            font-size: 13px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            margin: 0px;
            text-align: left;
            word-break: keep-all;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            &.list {
              flex: none;
              width: 400px;
            }
          }
        }

        .table-row {
          display: flex;
          width: fit-content;
          justify-content: space-around;
          min-width: 100%;
          gap: 5px;
          position: relative;
          background-color: #fff;
          cursor: pointer;

          .action-block {
            position: sticky;
            background-color: #fff;
            left: -1px;
            top: 0;
            width: 42px;
            height: 42px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          &:nth-child(even) {
            background: #f6f8f8;

            .action-block {
              background: #f6f8f8;
            }
          }

          .cell {
            padding: 8px 0px;
            min-width: 220px;
            width: fit-content;
            max-width: 500px;
            box-sizing: border-box;
            white-space: nowrap;
            color: #19283d;
            font-family: "Inter";
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 22px; /* 183.333% */
            margin: 0px;
            word-break: keep-all;
            white-space: nowrap;
            text-overflow: ellipsis;

            .list-block {
              width: 390px;
              display: flex;
              padding-right: 5px;
              gap: 5px;
              flex-wrap: wrap;
              display: flex;
              flex-direction: column;
              gap: 5px;

              div {
                display: flex;
                gap: 5px;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: center;
                border-radius: 4px;
                padding: 5px 10px;
                border: 1px solid #d9dee1;
                cursor: default;

                span {
                  color: #19283d;
                  font-family: "Inter";
                  font-size: 12px;
                  font-style: normal;
                  font-weight: 400;
                  line-height: 22px; /* 183.333% */
                }
              }

              button {
                margin-top: 2px;
                background-color: transparent;
                border: none;
                padding: 6px;
                font-size: 0.9em;
                color: #2563eb;
                border-radius: 4px;
                cursor: pointer;
                transition: background-color 0.2s;

                &:hover {
                  background: #eff6ff;
                  text-decoration: underline;
                }
              }
            }
          }
        }
      }
    }

    .loading-data {
      display: block;
      width: 100%;
      margin: auto;
      box-sizing: border-box;
      padding: 80px 0px;
      font-family: "Inter";
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      color: #797e86;
      text-align: center;
    }

    .hyphen-table-footer {
      padding: 13px;
      display: flex;
      justify-content: flex-end;

      .pagination {
        display: inline-flex;
        justify-content: center;
        align-items: center;

        button {
          border: 1.8px solid #d9dee1;
          border-radius: 5px;
          background-color: #fff;
          color: #19283d;
          font-family: "Inter";
          font-size: 12px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
          padding: 5px 10px;
          margin: 0px 3px;
          cursor: pointer;

          &:hover {
            background-color: #d9dee1;
          }

          &.active {
            background-color: #19283d;
            color: #fff;
          }
        }
      }
    }
  }

  &__error-text {
    font-size: 12px;
    color: #ff0000;
  }
}

.hyphen-checkbox {
  display: block;
  position: relative;
  cursor: pointer;
  font-size: 22px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  font-size: 14px;
  font-style: normal;
  line-height: normal;
  font-weight: 500;
  pointer-events: none;
  color: rgba(25, 40, 61, 0.8);

  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .checkmark {
      background-color: var(--primary-color, #19283d);
    }

    &:hover ~ .checkmark {
      background-color: rgba(25, 40, 61, 0.7);
    }

    &:checked ~ .checkmark:after {
      display: block;
    }
  }

  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 19px;
    width: 19px;
    border-radius: 3px;
    border: 1px solid #d9dee1;
    transform: translate(-10px, -10px);

    &:after {
      content: "";
      position: absolute;
      display: none;
    }

    &:after {
      left: 6px;
      top: 2px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 3px 3px 0;
      border-radius: 2px;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
}
</style>
